/* Vimaana Garten Team — Screens (Port aus Design-Bundle, an Live-API gebunden). */
import { useEffect, useState } from "react";
import {
  VGTIcon, eur, std, mapsUrl, wtag, tag, monatKurz, langDatum, relTag, relZeit,
  jobVerdienst, stelleVerdienst, rechnungAnalysieren,
  type TeamModel, type Hour, type Einsatz, type Job, type RechnungV, type ProjektOption, type RechnungUploadArgs,
  type FotoUploadArgs, type Projektpreis,
} from "./lib/vgt";
import { EinsatzAntwort } from "./components/EinsatzAntwort";
import { ProjektAuswahl } from "./components/ProjektAuswahl";
import { InstallBanner } from "./components/InstallBanner";
import { FeedbackForm, MVersionsZeile, apiDelete, apiGet, apiPost, useTheme } from "@vimaana/ui";
import type { FeedbackFormPayload, FeedbackPage } from "@vimaana/ui";
import { abmelden, anmelden, hinweisZurLage, lage, type PushLage } from "./lib/push";
import { useAufgabeToggle, useAufgaben } from "./lib/team";

// ─────────────────────────── Atoms ────────────────────────────────────────
/** Kunde eines Einsatzes als Meta-Zeile — der Helfer soll wissen, bei wem er
 *  klingelt, nicht nur wo. Rendert nichts, wenn kein Kunde hinterlegt ist. */
function KundeMeta({ kunde }: { kunde: string }) {
  if (!kunde) return null;
  return <span className="vgt-metaitem"><VGTIcon name="person" /> {kunde}</span>;
}
/** Der Einsatzort — eine Zeile, die die Karten-App des Telefons öffnet.
 *  Bewusst KEINE gemalte Karte in der App: sie kostete die halbe Kartenhöhe,
 *  zeigte nichts Echtes und drückte alles Wichtige nach unten. */
function MapLink({ adresse }: { adresse: string }) {
  if (!adresse) return null;
  return (
    <a className="vgt-ort" href={mapsUrl(adresse)} target="_blank" rel="noopener noreferrer"
       onClick={(e) => e.stopPropagation()}>
      <VGTIcon name="pin" />{adresse}
    </a>
  );
}
// ─────────────────────────── Start ────────────────────────────────────────
// ─────────────────────────── Stempeluhr ───────────────────────────────────
/** Verdienst-Zeile an einem Einsatz/Job. Stundenlohn × geplante Netto-Stunden,
 *  oder — wenn der Termin an einem bestaetigten Projektpreis haengt — die
 *  Pauschale samt Anzahl der Termine, fuer die sie gilt. Bewusst KEINE
 *  anteilige Aufteilung: die Pauschale ist fuers Projekt vereinbart, nicht
 *  je Termin, und eine erfundene Teilung waere eine falsche Zusage. */
function VerdienstZeile({ v, klein }: { v: ReturnType<typeof jobVerdienst>; klein?: boolean }) {
  if (!v) return null;
  return (
    <div className={"vgt-verdienst" + (v.art === "pauschale" ? " is-pauschale" : "") + (klein ? " is-klein" : "")}>
      <VGTIcon name="euro" />
      <b>{v.betragText}</b>
      <span className="d">{v.detail}</span>
    </div>
  );
}

/** Wählt aus den heutigen Einsätzen den *einen*, um den es gerade geht: den Slot,
 *  dessen Fenster jetzt läuft; sonst den nächsten heute anstehenden; sonst den
 *  zuletzt beendeten. So sieht der Helfer nicht alle Tageseinsätze gleichzeitig,
 *  sondern den, an dem er dran ist. */
function heutigerEinsatz(list: Einsatz[]): Einsatz[] {
  const toMin = (t: string) => { const [hh, mm] = (t || "").split(":").map(Number); return (hh || 0) * 60 + (mm || 0); };
  const now = new Date();
  const jetzt = now.getHours() * 60 + now.getMinutes();
  const drin = list.filter(e => toMin(e.von) <= jetzt && jetzt <= toMin(e.bis));
  if (drin.length) return [drin.sort((a, b) => toMin(b.von) - toMin(a.von))[0]];
  const kommend = list.filter(e => toMin(e.von) > jetzt).sort((a, b) => toMin(a.von) - toMin(b.von));
  if (kommend.length) return [kommend[0]];
  const vorbei = [...list].sort((a, b) => toMin(b.bis) - toMin(a.bis));
  return vorbei.length ? [vorbei[0]] : [];
}

/** Der heutige Einsatz auf der Start-Seite, mit dem einen Knopf, um den es am
 *  Feierabend geht.
 *
 *  Bis 20.08.2026 stand hier eine Stempeluhr. Sie ist weg (Entscheidung
 *  Benjamin): Ein- und Ausstempeln wurde zu oft vergessen, und ein vergessener
 *  Stempel kostet einen ganzen Arbeitstag an falscher Zeit. Jetzt trägt der
 *  Helfer Beginn, Ende und Pause ein — vorbelegt mit dem Plan, sodass ein Tag,
 *  der wie geplant lief, zwei Tipps kostet. */
function HeuteZeitPanel({ einsaetze, onZeit, onFotos, busy }: {
  einsaetze: Einsatz[]; onZeit: (e: Einsatz) => void;
  onFotos: (a: FotoZiel) => void; busy: boolean;
}) {
  // ALLE heutigen Einsätze, nicht nur die ohne erfasste Zeit. Bis 24.08.2026
  // verschwand die Karte, sobald die Zeit eingetragen war — und mit ihr der
  // kürzeste Weg zu den Fotos, ausgerechnet am Feierabend, wenn man sie hat.
  const heute = heutigerEinsatz(einsaetze.filter(e => e.heute));
  if (heute.length === 0) return null;
  return (
    <>
      <div className="vgt-seclabel">Heute</div>
      {heute.map(e => {
        const offen = e.status === "geplant";
        return (
          <div key={e.id} className="vgt-einsatz" style={{ marginBottom: ".6rem" }}>
            <div className="vgt-einsatz-top">
              <span style={{ width: 44, height: 44, borderRadius: 13, flex: "none", display: "grid", placeItems: "center", background: "var(--bg-3)", color: "var(--text-2)" }}>
                <VGTIcon name="clock" style={{ width: 22, height: 22 }} />
              </span>
              <div className="vgt-einsatz-main">
                <div className="vgt-einsatz-proj">{e.projekt}</div>
                <div className="vgt-einsatz-meta">
                  <span className="vgt-metaitem"><VGTIcon name="clock" /> geplant {e.von}–{e.bis}</span>
                  <KundeMeta kunde={e.kunde} />
                </div>
                <VerdienstZeile v={jobVerdienst(e)} klein />
                <MapLink adresse={e.adresse} />
              </div>
            </div>

            {/* Zwei Dinge, die am Tag selbst anfallen: die Zeit und die Bilder.
                Ist die Zeit schon drin, rückt „Fotos" nach vorn — dann ist es
                das Einzige, was hier noch zu tun ist. */}
            <div className="vgt-actions" style={{ marginTop: ".6rem" }}>
              {offen && (
                <button className="vgt-cta" disabled={busy} onClick={() => onZeit(e)}>
                  <VGTIcon name="clock" /> Zeit eintragen
                </button>
              )}
              {e.projektId && (
                <button className={"vgt-cta" + (offen ? " is-soft" : "")}
                        onClick={() => onFotos({ projektId: e.projektId, slotId: e.id, datum: e.datum })}>
                  <VGTIcon name="camera" /> Fotos
                </button>
              )}
            </div>

            {!offen && (
              <div className="vgt-heute-notiz">
                {e.status === "zu_bestaetigen"
                  ? "Deine Zeit wartet noch auf dich — unter Stunden."
                  : e.status === "korrektur"
                    ? "Zeit ist eingetragen, wir schauen kurz drauf."
                    : "Zeit ist notiert."}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export function AuslagenSheet({ h, onClose, onSubmit, busy }: {
  h: Hour; onClose: () => void; onSubmit: (betrag: number, note: string) => void; busy: boolean;
}) {
  const [betrag, setBetrag] = useState(h.zusatzkosten > 0 ? h.zusatzkosten.toFixed(2).replace(".", ",") : "");
  const [note, setNote] = useState(h.note || "");
  const num = Math.max(0, parseFloat(betrag.replace(",", ".")) || 0);
  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Auslagen eintragen</div>
        <div className="vgt-sheet-sub">{h.projekt} · {h.datum && new Date(h.datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}. Trag ein, was du selbst verauslagt hast (z. B. Anfahrt, Material, Parken).</div>
        <div>
          <div className="vgt-field-label">Betrag (€)</div>
          <input className="vgt-input" inputMode="decimal" placeholder="0,00" value={betrag} onChange={e => setBetrag(e.target.value)} />
        </div>
        <div>
          <div className="vgt-field-label">Wofür? (optional)</div>
          <textarea className="vgt-textarea" placeholder="z. B. Anfahrt 50 km, Parkticket" value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Ist sofort gespeichert. Wir sehen es am Job und rechnen es mit ab.</div></div>
        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy} onClick={() => onSubmit(num, note.trim())}><VGTIcon name="check" /> Speichern</button>
        </div>
      </div>
    </div>
  );
}

/** „Heute" — beantwortet genau eine Frage: worauf tippe ich jetzt?
 *  Bewusst KEINE Vorschau auf Plan, Stunden oder Geld; jede Doppelung hat den
 *  Helfer bisher raten lassen, welche der beiden Ansichten die vollstaendige
 *  ist. Was hier steht, ist entweder heute faellig oder wartet auf eine
 *  Reaktion. */
export function VGTHeute({ m, onGoTab, onOpenEinsatz, onOpenNotifs, onOpenProfil, onZeit, onFotos, unread }: {
  m: TeamModel; onGoTab: (t: string) => void; onOpenEinsatz: (id: string) => void; onOpenNotifs: () => void;
  onOpenProfil: () => void; onZeit: (e: Einsatz) => void; onFotos: (a: FotoZiel) => void; unread: number;
}) {
  const { user, summary } = m;
  if (!user) return null;
  const stunde = new Date().getHours();
  const gruss = stunde < 11 ? "Guten Morgen" : stunde < 17 ? "Guten Tag" : "Guten Abend";
  const heutige = m.einsaetze.filter(e => e.heute);
  const n = summary.naechster;
  // Dieselbe Grenze wie in `HeuteZeitPanel`: die Karte steht den ganzen Tag,
  // auch wenn die Zeit schon drin ist — der Weg zu den Fotos hängt daran.
  const hatHeuteKarte = heutige.length > 0;

  return (
    <div className="vgt-screen">
      <div className="vgt-head">
        <div className="vgt-head-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="vgt-greet">{gruss}, <b>{user.vorname}</b>.</div>
            <div className="vgt-greet-date">{langDatum(new Date().toISOString().slice(0, 10))}</div>
          </div>
          <button className="vgt-iconbtn" onClick={onOpenNotifs} title="Benachrichtigungen"><VGTIcon name="bell" />{unread > 0 && <span className="ind" />}</button>
          <button className="vgt-iconbtn" onClick={onOpenProfil} title="Profil" style={{ padding: 0, overflow: "hidden" }}>
            <span className="vgt-av sm" style={{ border: 0, borderRadius: 11, width: "100%", height: "100%" }}>{user.initialen}</span>
          </button>
        </div>
      </div>
      <div className="vgt-scroll"><div className="vgt-pad">
        <HeuteZeitPanel einsaetze={m.einsaetze} onZeit={onZeit} onFotos={onFotos} busy={m.busy} />

        {/* Nichts heute? Dann wenigstens sagen, wann es weitergeht — als Zeile,
            nicht als zweite Einsatzliste neben dem Einsätze-Tab. */}
        {!hatHeuteKarte && (
          n ? (
            <button className="vgt-naechster" onClick={() => onOpenEinsatz(n.id)}>
              <span className="ic"><VGTIcon name="calendar" /></span>
              <span className="m">
                <span className="l">Heute frei · als Nächstes</span>
                <span className="v">{relTag(n.datum) || langDatum(n.datum)} · {n.von} Uhr · {n.projekt}</span>
              </span>
              <span className="chev"><VGTIcon name="chev" /></span>
            </button>
          ) : <div className="vgt-empty">Heute frei — und auch sonst steht gerade nichts an.<br />Schau mal bei den offenen Stellen, ob was für dich dabei ist.</div>
        )}

        {summary.einladungenOffen > 0 && (
          <button className="vgt-einsatz" style={{ borderColor: "var(--amber)" }} onClick={() => onGoTab("einsaetze")}>
            <div className="vgt-einsatz-top">
              <span className="vgt-kachel is-amber"><VGTIcon name="calendar" style={{ width: 22, height: 22 }} /></span>
              <div className="vgt-einsatz-main">
                <div className="vgt-einsatz-proj">{summary.einladungenOffen} {summary.einladungenOffen === 1 ? "Job wartet auf dich" : "Jobs warten auf dich"}</div>
                <div className="vgt-einsatz-task">Sag kurz Bescheid, ob du kannst.</div>
              </div>
              <span className="vgt-reltag is-now">Antworten</span>
            </div>
          </button>
        )}

        {summary.toConfirm > 0 && (
          <button className="vgt-einsatz" style={{ borderColor: "var(--accent-line)" }} onClick={() => onGoTab("stunden")}>
            <div className="vgt-einsatz-top">
              <span className="vgt-kachel is-accent"><VGTIcon name="clock" style={{ width: 22, height: 22 }} /></span>
              <div className="vgt-einsatz-main">
                <div className="vgt-einsatz-proj">{summary.toConfirm} {summary.toConfirm === 1 ? "Arbeitszeit wartet" : "Arbeitszeiten warten"} auf dich</div>
                <div className="vgt-einsatz-task">Schau kurz drauf: passt die Zeit so?</div>
              </div>
              <span className="vgt-reltag is-now">Prüfen</span>
            </div>
          </button>
        )}
      </div></div>
    </div>
  );
}

// ─────────────────────────── Einsätze ────────────────────────────────────
/** Eine Liste, nach Datum sortiert — eingeplante Jobs und freie Stellen
 *  stehen untereinander und sehen gleich aus. Der frühere Umschalter
 *  „Meine / Offen" war eine Unterscheidung, die den Helfer nichts angeht:
 *  ihn interessiert, wann er wo arbeiten kann (Entscheidung 10.08.2026).
 *  Unterschiedlich ist nur der Fuß der Karte — zusagen/absagen beim eigenen
 *  Job, bewerben bei der freien Stelle. */
type Zeile =
  | { art: "einsatz"; key: string; datum: string; einsatz: Einsatz }
  | { art: "stelle"; key: string; datum: string; job: Job };

export function VGTEinsaetze({ m, onOpenEinsatz, onZusage, onAbsage, onInteresse }: {
  m: TeamModel; onOpenEinsatz: (id: string) => void;
  onZusage: (id: string) => void; onAbsage: (e: Einsatz) => void; onInteresse: (job: Job) => void;
}) {
  const zeilen: Zeile[] = [
    ...m.einsaetze.map(e => ({ art: "einsatz" as const, key: "e" + e.id, datum: e.datum, einsatz: e })),
    // Abgelehnte Bewerbungen stehen an ihrem Datum mit — leise, ohne Knöpfe.
    ...m.abgelehnteBewerbungen.map(e => ({ art: "einsatz" as const, key: "e" + e.id, datum: e.datum, einsatz: e })),
    ...m.jobs.map(j => ({ art: "stelle" as const, key: "j" + j.id, datum: j.datum, job: j })),
  ].sort((x, y) => x.datum.localeCompare(y.datum));

  const frei = m.jobs.length;
  return (
    <div className="vgt-screen">
      <div className="vgt-head"><div className="vgt-head-top">
        <h1 className="vgt-h1">Einsätze<span className="sub">
          {m.einsaetze.length} geplant{frei > 0 ? ` · ${frei} ${frei === 1 ? "freie Stelle" : "freie Stellen"}` : ""}
        </span></h1>
      </div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        {zeilen.length === 0 && <div className="vgt-empty">Gerade steht nichts an.<br />Sobald wir dich einplanen, siehst du es hier.</div>}
        {zeilen.map(z => z.art === "einsatz" ? (
          <div key={z.key} className={"vgt-einsatz" + (z.einsatz.einsatzStatus === "abgelehnt_vom_inhaber" ? " is-still" : "")}
               onClick={() => onOpenEinsatz(z.einsatz.id)}
               role="button" tabIndex={0} onKeyDown={ev => ev.key === "Enter" && onOpenEinsatz(z.einsatz.id)}>
            <div className="vgt-einsatz-top">
              <div className="vgt-datechip"><div className="d">{tag(z.einsatz.datum)}</div><div className="m">{monatKurz(z.einsatz.datum)}</div><div className="w">{wtag(z.einsatz.datum)}</div></div>
              <div className="vgt-einsatz-main">
                <div className="vgt-einsatz-proj">{z.einsatz.projekt}</div>
                <div className="vgt-einsatz-task">{z.einsatz.taetigkeit}</div>
                <div className="vgt-einsatz-meta"><span className="vgt-metaitem"><VGTIcon name="clock" /> {z.einsatz.von}–{z.einsatz.bis}</span><KundeMeta kunde={z.einsatz.kunde} /></div>
                <VerdienstZeile v={jobVerdienst(z.einsatz)} />
                {z.einsatz.verschoben && <div className="vgt-verlegt">{z.einsatz.verschoben}</div>}
                <MapLink adresse={z.einsatz.adresse} />
              </div>
              {relTag(z.einsatz.datum) && <span className={"vgt-reltag" + (["Heute", "Morgen"].includes(relTag(z.einsatz.datum)!) ? " is-now" : "")}>{relTag(z.einsatz.datum)}</span>}
            </div>
            <EinsatzAntwort status={z.einsatz.einsatzStatus} grund={z.einsatz.absageGrund} busy={m.busy}
                            onZusage={() => onZusage(z.einsatz.id)} onAbsage={() => onAbsage(z.einsatz)} />
          </div>
        ) : (
          <div key={z.key} className="vgt-einsatz">
            <div className="vgt-einsatz-top">
              <div className="vgt-datechip"><div className="d">{tag(z.job.datum)}</div><div className="m">{monatKurz(z.job.datum)}</div><div className="w">{wtag(z.job.datum)}</div></div>
              <div className="vgt-einsatz-main">
                <div className="vgt-einsatz-proj">{z.job.projekt}</div>
                {z.job.beschreibung && <div className="vgt-einsatz-task">{z.job.beschreibung}</div>}
                <div className="vgt-einsatz-meta">
                  <span className="vgt-metaitem"><VGTIcon name="clock" /> {z.job.von}–{z.job.bis}</span>
                  <KundeMeta kunde={z.job.kunde} />
                  <span className="vgt-metaitem"><VGTIcon name="team" /> {z.job.frei} von {z.job.plaetze} frei</span>
                </div>
                <VerdienstZeile v={stelleVerdienst(z.job)} />
                <MapLink adresse={z.job.adresse} />
              </div>
              {relTag(z.job.datum) && <span className={"vgt-reltag" + (["Heute", "Morgen"].includes(relTag(z.job.datum)!) ? " is-now" : "")}>{relTag(z.job.datum)}</span>}
            </div>
            <EinsatzAntwort offeneStelle busy={m.busy} onZusage={() => onInteresse(z.job)} onAbsage={() => {}} />
          </div>
        ))}
      </div></div>
    </div>
  );
}

// ─────────────────────────── Stunden ──────────────────────────────────────
const STATUS_TEXT: Record<string, string> = {
  zu_bestaetigen: "Bitte prüfen", korrektur: "Liegt bei uns",
  bestaetigt: "Passt", abgelehnt: "Nicht übernommen",
};
const STATUS_KLASSE: Record<string, string> = {
  zu_bestaetigen: "tobe", korrektur: "korr", bestaetigt: "ok", abgelehnt: "no",
};

/** Eine Zeile je Arbeitstag. Karten haben pro Eintrag einen halben Bildschirm
 *  gekostet — bei zwanzig Tagen sieht man dann nichts mehr. Details erscheinen
 *  erst beim Antippen, dort auch die Aktionen und die Foto-Dokumentation:
 *  Fotos gehören dorthin, wo der Helfer den Tag ohnehin gerade durchgeht. */
function StundenZeile({ h, offen, onToggle, onConfirm, onZeit, onAuslagen, onFotos, busy }: {
  h: Hour; offen: boolean; onToggle: () => void;
  onConfirm: (id: string) => void; onZeit: (h: Hour) => void;
  onAuslagen: (h: Hour) => void; onFotos: (a: FotoZiel) => void; busy: boolean;
}) {
  const perPauschale = h.verguetungsart === "pauschale" && !!h.pauschaleId;
  const wartet = h.status === "zu_bestaetigen";
  // Eintragen geht, solange die Zeit nicht bestätigt ist — also auch an einem
  // Tag, den wir zurückgegeben haben (Wunsch Benjamin, 20.08.2026). Ist sie
  // bestätigt, ist die Zahl vereinbart; dann sagt man uns lieber Bescheid.
  const darfEintragen = h.status !== "bestaetigt";
  return (
    <div className={"vgt-sz" + (offen ? " is-offen" : "") + (wartet ? " is-tobe" : "")}>
      <button className="vgt-sz-kopf" onClick={onToggle} aria-expanded={offen}>
        <span className="tag">{h.datum ? new Date(h.datum + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "—"}</span>
        <span className="mitte">
          <span className="proj">{h.projekt}</span>
          <span className={"pkt " + (STATUS_KLASSE[h.status] || "")} title={STATUS_TEXT[h.status]} />
        </span>
        <span className="std">{std(h.erfasstStd)}</span>
        <span className="betrag">{perPauschale ? "pausch." : eur(h.betrag)}</span>
        <span className="chev"><VGTIcon name="chev" /></span>
      </button>

      {offen && (
        <div className="vgt-sz-detail">
          <div className="vgt-sz-zeilen">
            <div><span className="l">Zeit</span><span className="v">{h.von}–{h.bis}{h.pause ? ` · ${std(h.pause)} Pause` : ""}</span></div>
            <div><span className="l">Status</span><span className="v">{STATUS_TEXT[h.status] || h.status}</span></div>
            {perPauschale
              ? <div><span className="l">Vergütung</span><span className="v">{eur(h.pauschaleBetrag)} über {h.pauschaleBezeichnung || "Projektpreis"}{h.pauschaleStatus && h.pauschaleStatus !== "bestaetigt" ? " · wartet auf Bestätigung" : ""}</span></div>
              : <div><span className="l">Betrag</span><span className="v">{eur(h.betrag)}</span></div>}
            {h.zusatzkosten > 0 && <div><span className="l">Auslagen</span><span className="v">{eur(h.zusatzkosten)}{h.note ? " · " + h.note : ""}</span></div>}
            {h.zusatzkosten === 0 && h.note && <div><span className="l">Notiz</span><span className="v">{h.note}</span></div>}
            {h.status === "abgelehnt" && <div><span className="l">Grund</span><span className="v">{h.ablehnungText || "Wir haben den Eintrag nicht übernommen."}</span></div>}
            {h.status === "korrektur" && <div><span className="l">Hinweis</span><span className="v">Deine Änderung liegt bei uns.</span></div>}
          </div>

          {darfEintragen && (
            <div className="vgt-actions">
              {/* „So gearbeitet" gibt es nur beim ersten Mal: es übernimmt die
                  geplante Zeit ungeprüft, und das ergibt nur Sinn, solange noch
                  nichts Erfasstes dasteht. */}
              {wartet && (
                <button className="vgt-cta is-green" disabled={busy} onClick={() => onConfirm(h.id)}><VGTIcon name="check" /> So gearbeitet</button>
              )}
              <button className={"vgt-cta" + (wartet ? " is-soft" : "")} onClick={() => onZeit(h)}>
                <VGTIcon name="edit" /> Zeit eintragen
              </button>
            </div>
          )}
          <div className="vgt-sz-mehr">
            <button className="vgt-linkbtn" disabled={busy} onClick={() => onAuslagen(h)}>
              <VGTIcon name="wallet" /> {h.zusatzkosten > 0 ? "Auslagen ändern" : "Auslagen eintragen"}
            </button>
            {h.projektId && (
              <button className="vgt-linkbtn" onClick={() => onFotos({ projektId: h.projektId, slotId: h.id })}>
                <VGTIcon name="camera" /> Fotos zum Projekt
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Eine vereinbarte Pauschale in der Stundentabelle.
 *
 *  Sie gehoert hierher, nicht auf die Geld-Seite: „was habe ich in diesem
 *  Monat verdient" ist eine Frage an das Stundenkonto, und ein Festpreis ist
 *  nichts anderes als Arbeit, die nicht nach Stunden bezahlt wird. Auf der
 *  Geld-Seite stand sie doppelt — einmal als Posten, einmal in der
 *  Guthabenzahl (Entscheidung 12.08.2026).
 *
 *  Ohne Aufklappen, weil es nichts zu tun gibt: die Bauleitung vereinbart
 *  Pauschalen, der Helfer nimmt sie zur Kenntnis. */
function PauschaleZeile({ p }: { p: Projektpreis }) {
  const fest = (p.status || "") === "bestaetigt";
  const frei = !!p.freigegeben;
  return (
    <div className="vgt-sz is-pausch">
      <div className="vgt-sz-kopf as-zeile">
        <span className="tag">{p.datum ? new Date(p.datum + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "—"}</span>
        <span className="mitte">
          <span className="proj">{p.projekt_title || "Projekt"}</span>
          <span className="pausch-tag">Pauschale</span>
        </span>
        <span className="std">{p.slot_count > 1 ? p.slot_count + " Termine" : ""}</span>
        <span className="betrag">{eur(p.betrag)}</span>
      </div>
      <div className="vgt-sz-fuss">
        {p.bezeichnung || "Festpreis fürs Projekt"}
        {fest ? "" : " · noch nicht fest"}
        {p.slot_count > 1 ? ` · gilt für alle ${p.slot_count} Termine zusammen` : ""}
        {/* Erst die Freigabe macht daraus Guthaben — sonst stünde ein Betrag da,
            auf den sich niemand verlassen kann. */}
        {fest && (frei ? " · freigegeben" : " · noch nicht freigegeben")}
      </div>
    </div>
  );
}

export function VGTStunden({ m, onConfirm, onZeit, onAuslagen, onNeu, onFotos }: {
  m: TeamModel; onConfirm: (id: string) => void; onZeit: (h: Hour) => void;
  onAuslagen: (h: Hour) => void; onNeu: () => void; onFotos: (a: FotoZiel) => void;
}) {
  const [offen, setOffen] = useState<string | null>(null);
  const wartend = m.hours.filter(h => h.status === "zu_bestaetigen").length;

  // Neueste zuerst, in Monatsblöcke geschnitten — so findet man einen Tag,
  // ohne zu scrollen und zu zählen. Stunden und Pauschalen laufen in EINER
  // Liste, weil beides dasselbe beantwortet: was habe ich wann verdient.
  type Posten =
    | { art: "stunde"; datum: string; h: Hour }
    | { art: "pauschale"; datum: string; p: Projektpreis };
  const posten: Posten[] = [
    ...m.hours.map(h => ({ art: "stunde" as const, datum: h.datum || "", h })),
    ...m.projektpreise.map(p => ({ art: "pauschale" as const, datum: p.datum || "", p })),
  ].sort((a, b) => b.datum.localeCompare(a.datum));

  const monate: { titel: string; eintraege: Posten[]; summeStd: number; summePausch: number }[] = [];
  for (const e of posten) {
    const titel = e.datum
      ? new Date(e.datum + "T12:00:00").toLocaleDateString("de-DE", { month: "long", year: "numeric" })
      : "Ohne Datum";
    let letzter = monate[monate.length - 1];
    if (!letzter || letzter.titel !== titel) {
      letzter = { titel, eintraege: [], summeStd: 0, summePausch: 0 };
      monate.push(letzter);
    }
    letzter.eintraege.push(e);
    if (e.art === "stunde") letzter.summeStd += e.h.erfasstStd;
    else letzter.summePausch += e.p.betrag;
  }

  return (
    <div className="vgt-screen">
      <div className="vgt-head"><div className="vgt-head-top">
        <h1 className="vgt-h1">Stunden<span className="sub">{wartend > 0 ? wartend + " warten auf dich" : "alles erledigt"}</span></h1>
      </div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        <button className="vgt-cta is-soft" style={{ width: "100%", marginBottom: 4 }} onClick={onNeu}>
          <VGTIcon name="plus" /> Zeit nachtragen
        </button>
        {posten.length === 0
          ? <div className="vgt-empty">Noch keine Zeiten da.<br />Nach deinem ersten Job stehen sie hier.</div>
          : monate.map(mo => (
            <div key={mo.titel} className="vgt-monat">
              <div className="vgt-monat-kopf">
                <span>{mo.titel}</span>
                <span className="s">
                  {mo.summeStd > 0 ? std(mo.summeStd) : ""}
                  {mo.summeStd > 0 && mo.summePausch > 0 ? " · " : ""}
                  {mo.summePausch > 0 ? eur(mo.summePausch) + " pauschal" : ""}
                </span>
              </div>
              {mo.eintraege.map(e => e.art === "stunde" ? (
                <StundenZeile key={e.h.id} h={e.h} busy={m.busy}
                  offen={offen === e.h.id} onToggle={() => setOffen(o => o === e.h.id ? null : e.h.id)}
                  onConfirm={onConfirm} onZeit={onZeit} onAuslagen={onAuslagen} onFotos={onFotos} />
              ) : (
                <PauschaleZeile key={"p" + e.p.id} p={e.p} />
              ))}
            </div>
          ))}
      </div></div>
    </div>
  );
}

// ─────────────────────────── Geld ─────────────────────────────────────────
/** Vorschau einer hinterlegten Rechnung — im Overlay, nicht in einem neuen
 *  Tab. Am Telefon ist ein zweiter Tab ein Ausflug, aus dem man selten
 *  zurückfindet; hier reicht ein Tippen daneben.
 *
 *  Die Datei wird **geholt und als Blob angezeigt**, nicht per URL in den
 *  Rahmen gehängt. Grund: die API antwortet auf jeder Route mit
 *  `X-Frame-Options: DENY`, und dieser Kopf verbietet die Darstellung im
 *  `<iframe>` auch bei gleicher Herkunft — die Vorschau blieb deshalb leer und
 *  zeigte nur das kaputte Dokumentsymbol (12.08.2026). Ein `blob:`-Verweis
 *  gehört dem Dokument selbst und kennt diese Sperre nicht. Nebenbei trägt der
 *  Download so den richtigen Dateinamen. */
function RechnungVorschau({ r, url, onClose }: { r: RechnungV; url: string; onClose: () => void }) {
  const [blob, setBlob] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  const [typ, setTyp] = useState("");

  useEffect(() => {
    let tot = false;
    let aktuell = "";
    setBlob(null); setFehler(false);
    fetch(url, { credentials: "include" })
      .then(a => { if (!a.ok) throw new Error(String(a.status)); setTyp(a.headers.get("content-type") || ""); return a.blob(); })
      .then(b => { if (tot) return; aktuell = URL.createObjectURL(b); setBlob(aktuell); })
      .catch(() => { if (!tot) setFehler(true); });
    return () => { tot = true; if (aktuell) URL.revokeObjectURL(aktuell); };
  }, [url]);

  // Der Dateiname entscheidet, der Content-Type ist die zweite Meinung.
  const istBild = /\.(jpe?g|png|heic|heif|webp)$/i.test(r.dateiname || "") || typ.startsWith("image/");

  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-vorschau" onClick={e => e.stopPropagation()}>
        <div className="vgt-vorschau-kopf">
          <div className="m">
            <strong>{eur(r.betrag)}</strong>
            <span>{r.datum ? new Date(r.datum + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : "ohne Datum"}
              {r.rechnungsart === "pauschale" ? " · Pauschalrechnung" : " · Stundenrechnung"}</span>
          </div>
          <a className="vgt-linkbtn" href={blob || url} download={r.dateiname || "rechnung"}
             target={blob ? undefined : "_blank"} rel="noopener noreferrer">Herunterladen</a>
          <button className="vgt-linkbtn" onClick={onClose}>Schließen</button>
        </div>
        <div className="vgt-vorschau-blatt">
          {fehler
            ? <div className="vgt-vorschau-hinweis">
                Die Datei lässt sich gerade nicht öffnen.<br />
                Über „Herunterladen" kommst du trotzdem dran.
              </div>
            : !blob
            ? <div className="vgt-vorschau-hinweis">lädt …</div>
            : istBild
            ? <img src={blob} alt={r.dateiname || "Rechnung"} />
            : <iframe src={blob} title={r.dateiname || "Rechnung"} />}
        </div>
      </div>
    </div>
  );
}

/** Der Zustand einer Rechnung aus Sicht des Helfers.
 *
 *  Maßgeblich ist die **Auszahlung**, nicht das Kennzeichen `is_bezahlt`:
 *  Kontor zeigt Altdaten mit gesetztem Haken, aber ohne Buchung, ausdrücklich
 *  als „bezahlt, nicht gebucht". Für den Helfer heißt bezahlt: Geld ist da.
 *  Deshalb gilt hier die Auszahlung zuerst und der Haken nur als Rückfall. */
function rechnungStatus(r: RechnungV): { ton: "ok" | "warte" | "pruef"; label: string; detail: string } {
  if (r.auszahlung_id) {
    const d = r.auszahlung_am
      ? new Date(r.auszahlung_am + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
      : "";
    return { ton: "ok", label: "Bezahlt", detail: d ? "überwiesen am " + d : "überwiesen" };
  }
  if (r.is_bezahlt) return { ton: "ok", label: "Bezahlt", detail: "Buchung wird noch nachgetragen" };
  if (r.status === "eingereicht") return { ton: "pruef", label: "Eingereicht", detail: "liegt bei uns zur Prüfung" };
  const stand: Record<string, string> = {
    erfasst: "geprüft und übernommen",
    verbucht: "verbucht",
    an_steuerberater_gesendet: "beim Steuerberater",
  };
  return { ton: "warte", label: "Angenommen", detail: (stand[r.status] || "in Bearbeitung") + " · noch nicht überwiesen" };
}

export function VGTGeld({ m, onUpload, onErsetzen, onLieferschein }: {
  m: TeamModel; onUpload: (file?: File) => void; onErsetzen: (belegId: string) => void;
  onLieferschein: () => void;
}) {
  const { user, summary, auszahlungen, rechnungen } = m;
  const [vorschau, setVorschau] = useState<RechnungV | null>(null);

  const offeneRechnungen = rechnungen.filter(r => !r.auszahlung_id && !r.is_bezahlt);
  const bezahlteRechnungen = rechnungen.filter(r => r.auszahlung_id || r.is_bezahlt);
  const offeneSumme = offeneRechnungen.reduce((s, r) => s + r.betrag, 0);
  // Auszahlungen, die an einer Rechnung hängen, stehen dort. Hier bleibt, was
  // ohne Rechnung geflossen ist — sonst liefe dieselbe Zahlung doppelt durchs Bild.
  const freieAuszahlungen = auszahlungen.filter(a => !a.belegId);

  return (
    <div className="vgt-screen">
      <div className="vgt-head"><div className="vgt-head-top">
        <h1 className="vgt-h1">Geld<span className="sub">Guthaben, Rechnungen, Auszahlungen</span></h1>
      </div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        <div className="vgt-balance">
          <div className="vgt-balance-label"><VGTIcon name="wallet" style={{ width: 14, height: 14 }} /> Das steht für dich offen</div>
          <div className="vgt-balance-num">{eur(summary.offenBetrag)}</div>
          <div className="vgt-balance-sub">
            {std(summary.offenStd)} bestätigt{summary.pauschaleSumme > 0 ? ` + ${eur(summary.pauschaleSumme)} Pauschale` : ""}
            {user ? ` · dein Satz: ${user.satz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/Std.` : ""}
          </div>
          <div className="vgt-balance-foot">
            <div className="vgt-balance-pill"><div className="v">{eur(summary.verdientGesamt)}</div><div className="l">verdient gesamt</div></div>
            <div className="vgt-balance-pill"><div className="v">{eur(summary.ausgezahltGesamt)}</div><div className="l">ausgezahlt</div></div>
          </div>
        </div>

        {/* ── Rechnungen: erst was noch aussteht, dann was erledigt ist ── */}
        <div className="vgt-seclabel">
          Meine Rechnungen <span className="c">· {rechnungen.length}</span>
        </div>
        {rechnungen.length === 0 && (
          <div className="vgt-empty">Noch keine Rechnung da.<br />Lad sie unten hoch — du siehst dann hier, wo sie steht.</div>
        )}

        {offeneRechnungen.length > 0 && (
          <div className="vgt-untertitel">Offen <span>{offeneRechnungen.length} · {eur(offeneSumme)}</span></div>
        )}
        {offeneRechnungen.map(r => (
          <RechnungZeile key={r.id} r={r} onVorschau={() => setVorschau(r)} onErsetzen={() => onErsetzen(r.id)} />
        ))}

        {bezahlteRechnungen.length > 0 && (
          <div className="vgt-untertitel">Bezahlt <span>{bezahlteRechnungen.length}</span></div>
        )}
        {bezahlteRechnungen.map(r => (
          <RechnungZeile key={r.id} r={r} onVorschau={() => setVorschau(r)} onErsetzen={() => onErsetzen(r.id)} />
        ))}

        <div className="vgt-seclabel">Rechnung schicken</div>
        <RechnungDropInline onPick={f => onUpload(f || undefined)} />

        {/* Lieferscheine sind kein Geld des Helfers — sie stehen hier, weil das
            der Ort ist, an dem er ohnehin Papier abgibt. */}
        <div className="vgt-seclabel">Lieferschein abgeben <span className="c">· für eine Baustelle</span></div>
        <button className="vgt-cta is-soft" onClick={onLieferschein}>
          <VGTIcon name="doc" /> Lieferschein hochladen
        </button>

        {freieAuszahlungen.length > 0 && (<>
          <div className="vgt-seclabel">Schon an dich überwiesen <span className="c">· ohne Rechnung</span></div>
          {freieAuszahlungen.map(a => (
            <div key={a.id} className="vgt-zeile">
              <div className="m">
                <div className="t">überwiesen am {a.datum && new Date(a.datum + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</div>
                {a.vermerk && <div className="s">{a.vermerk}</div>}
              </div>
              <div className="b">− {eur(a.betrag)}</div>
            </div>
          ))}
        </>)}
      </div></div>

      {vorschau && (
        <RechnungVorschau r={vorschau} url={m.rechnungDateiUrl(vorschau.id)} onClose={() => setVorschau(null)} />
      )}
    </div>
  );
}

/** Eine Rechnung als Zeile: Betrag und Status auf einen Blick, Datei und
 *  Ersetzen als ruhige Textaktionen darunter. */
function RechnungZeile({ r, onVorschau, onErsetzen }: {
  r: RechnungV; onVorschau: () => void; onErsetzen: () => void;
}) {
  const st = rechnungStatus(r);
  return (
    <div className="vgt-zeile is-block">
      <div className="vgt-zeile-top">
        <div className="m">
          <div className="t">
            {r.datum ? new Date(r.datum + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "ohne Datum"}
            <span className="art">{r.rechnungsart === "pauschale" ? "Pauschale" : "Stunden"}</span>
          </div>
          <div className="s">{st.detail}{r.projekt_title ? " · " + r.projekt_title : ""}</div>
        </div>
        <div className="b">{eur(r.betrag)}</div>
      </div>
      <div className="vgt-zeile-fuss">
        <span className={"vgt-pill " + (st.ton === "ok" ? "ok" : st.ton === "warte" ? "korr" : "tobe")}>
          <span className="dot" />{st.label}
        </span>
        {r.hat_datei && <button className="vgt-linkbtn" onClick={onVorschau}><VGTIcon name="doc" /> Ansehen</button>}
        {r.ersetzbar && <button className="vgt-linkbtn" onClick={onErsetzen}><VGTIcon name="arrowup" /> Ersetzen</button>}
      </div>
      {!r.hat_datei && <div className="vgt-zeile-hinweis">Zu dieser Rechnung liegt bei uns keine Datei.</div>}
    </div>
  );
}

function RechnungDropInline({ onPick }: { onPick: (file: File | null) => void }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      className={"vgt-drop" + (drag ? " is-drag" : "")}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); onPick(e.dataTransfer.files?.[0] || null); }}
    >
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/*" hidden
             onChange={e => { onPick(e.target.files?.[0] || null); e.target.value = ""; }} />
      <div className="vgt-drop-empty">
        <span className="ic"><VGTIcon name="arrowup" /></span>
        <div className="t">Rechnung hierher ziehen<br /><span className="sub">oder klicken zum Auswählen · PDF, JPG, PNG</span></div>
      </div>
    </label>
  );
}

// ─────────────────────────── Einsatz-Detail ───────────────────────────────
/** Was ein Helfer vor dem Einsatz wissen muss — und sonst nichts: bei wem,
 *  wo (als Link in die Karten-App, nicht als Attrappe in der App), wann und
 *  was es bringt. Dazu die Zu-/Absage und der Foto-Upload fuers Projekt. */
/** Was an diesem Einsatz zu tun ist — die Leistungen des Arbeitstags aus Kontor
 *  (Plan Projekt-Tracking, Etappe 7) und Pflegeaufgaben. Abgehakt wird mit Namen;
 *  Kontor sieht denselben Haken. */
function EinsatzCheckliste({ terminId }: { terminId: string | null }) {
  const q = useAufgaben(terminId);
  const toggle = useAufgabeToggle();
  const liste = q.data || [];
  if (!terminId || !liste.length) return null;
  const fertig = liste.filter((a) => a.erledigt_am).length;
  return (<>
    <div className="vgt-sec-head"><VGTIcon name="check" /> Was zu tun ist <span className="c">· {fertig} von {liste.length} erledigt</span></div>
    <div className="vgt-tasklist">
      {liste.map((a) => (
        <button key={a.id} type="button" className={"vgt-task" + (a.erledigt_am ? " done" : "")}
          disabled={toggle.isPending} aria-pressed={!!a.erledigt_am}
          onClick={() => toggle.mutate(a.id)}>
          <span className="box">{a.erledigt_am ? "✓" : ""}</span>
          <span className="lbl">{a.titel}
            {a.erledigt_am && <small className="vgt-task-wer"> · {a.erledigt_von || "erledigt"}</small>}
          </span>
        </button>
      ))}
    </div>
    {toggle.isError && <div className="vgt-hinweis" style={{ marginTop: 8 }}>Konnte nicht gespeichert werden — bitte noch einmal tippen.</div>}
  </>);
}

export function VGTEinsatz({ einsatz: e, onBack, onFotos, onLieferschein, onZusage, onAbsage, busy }: {
  einsatz: Einsatz; onBack: () => void; onFotos: (a: FotoZiel) => void;
  onLieferschein: (projektId: string) => void;
  onZusage: (id: string) => void; onAbsage: (id: string) => void; busy: boolean;
}) {
  return (
    <div className="vgt-screen">
      <div className="vgt-detail-head">
        <button className="vgt-backbtn" onClick={onBack}><VGTIcon name="back" /></button>
        <div className="vgt-detail-title">{e.projekt}</div>
      </div>
      <div className="vgt-scroll"><div className="vgt-pad">
        <div className="vgt-einsatz">
          <div className="vgt-einsatz-top">
            <div className="vgt-datechip is-next"><div className="d">{tag(e.datum)}</div><div className="m">{monatKurz(e.datum)}</div><div className="w">{wtag(e.datum)}</div></div>
            <div className="vgt-einsatz-main">
              <div className="vgt-einsatz-proj">{e.taetigkeit}</div>
              <div className="vgt-einsatz-meta">
                <span className="vgt-metaitem"><VGTIcon name="clock" /> {e.von}–{e.bis} Uhr</span>
                <KundeMeta kunde={e.kunde} />
              </div>
              <VerdienstZeile v={jobVerdienst(e)} />
            </div>
            {relTag(e.datum) && <span className={"vgt-reltag" + (["Heute", "Morgen"].includes(relTag(e.datum)!) ? " is-now" : "")}>{relTag(e.datum)}</span>}
          </div>
          {e.verschoben && <div className="vgt-verlegt">{e.verschoben}</div>}
          <MapLink adresse={e.adresse} />
          <EinsatzAntwort status={e.einsatzStatus} grund={e.absageGrund} busy={busy} onZusage={() => onZusage(e.id)} onAbsage={() => onAbsage(e.id)} />
        </div>
        {e.hinweis && <div className="vgt-hinweis" style={{ marginTop: 8 }}><span className="pin"><VGTIcon name="info" /></span><div>{e.hinweis}</div></div>}
        <EinsatzCheckliste terminId={e.terminId} />
        {e.projektId && (<>
          <div className="vgt-sec-head"><VGTIcon name="camera" /> Fotos vom Job</div>
          <button className="vgt-cta is-soft" onClick={() => onFotos({ projektId: e.projektId, slotId: e.id })}>
            <VGTIcon name="camera" /> Fotos zu diesem Projekt hochladen
          </button>
          {/* Material kommt auf der Baustelle an — der Lieferschein gehoert
              genau hier hin, wo die Baustelle schon feststeht. */}
          <div className="vgt-sec-head"><VGTIcon name="doc" /> Material</div>
          <button className="vgt-cta is-soft" onClick={() => onLieferschein(e.projektId)}>
            <VGTIcon name="doc" /> Lieferschein abgeben
          </button>
        </>)}
      </div></div>
    </div>
  );
}

// ─────────────────────────── Profil ───────────────────────────────────────
/** Mitteilungen an- und abschalten — ein Schalter, ein Testknopf, ehrliche Hinweise.
 *
 *  Die Erlaubnis wird NUR aus dem Tippen heraus angefragt. Fragt eine Seite
 *  ungefragt beim Laden, sperrt Safari die Domain dauerhaft.
 *  Konzept: docs/system/KONZEPT-TEAM-PUSH.md
 */
function VGTMitteilungen() {
  const [l, setL] = useState<PushLage>("nicht_gefragt");
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [versandbereit, setVersandbereit] = useState(true);

  useEffect(() => {
    let aktiv = true;
    lage().then((neu) => { if (aktiv) setL(neu); });
    apiGet<{ public_key: string; versandbereit: boolean }>("/api/team/push/config")
      .then((k) => { if (aktiv) setVersandbereit(!!k.versandbereit); })
      .catch(() => { /* offline — der Schalter sagt es beim Tippen */ });
    return () => { aktiv = false; };
  }, []);

  const einschalten = async () => {
    setBusy(true); setMeldung("");
    try {
      const k = await apiGet<{ public_key: string }>("/api/team/push/config");
      const abo = await anmelden(k.public_key);
      await apiPost("/api/team/push/abos", abo);
      setL(await lage());
      setMeldung("Alles klar — wir melden uns.");
    } catch (err) {
      setMeldung((err as Error)?.message || "Hat nicht geklappt — probier es noch mal");
      setL(await lage());
    } finally { setBusy(false); }
  };

  const ausschalten = async () => {
    setBusy(true); setMeldung("");
    try {
      const endpunkt = await abmelden();
      await apiDelete(`/api/team/push/abos?endpoint=${encodeURIComponent(endpunkt)}`);
      setL(await lage());
      setMeldung("Auf diesem Gerät ist jetzt Ruhe.");
    } catch {
      setMeldung("Hat nicht geklappt — probier es noch mal");
    } finally { setBusy(false); }
  };

  const testen = async () => {
    setBusy(true); setMeldung("");
    try {
      const r = await apiPost<{ gesendet: number }>("/api/team/push/test");
      setMeldung(r.gesendet > 0
        ? "Ist raus — schau auf deinen Sperrbildschirm."
        : "Wir konnten nichts schicken. Schalte die Mitteilungen einmal aus und wieder an.");
    } catch {
      setMeldung("Hat nicht geklappt — probier es noch mal");
    } finally { setBusy(false); }
  };

  const kannSchalten = (l === "nicht_gefragt" || l === "aus" || l === "an") && versandbereit;

  return (
    <>
      <div className="vgt-seclabel">Mitteilungen</div>
      <div className="vgt-card">
        <button
          className="vgt-row"
          disabled={busy || !kannSchalten}
          onClick={l === "an" ? ausschalten : einschalten}
        >
          <span className="ic"><VGTIcon name="bell" /></span>
          <span className="rt">Sag mir Bescheid</span>
          <span className="rv">{l === "an" ? "An · ausschalten" : "Aus · einschalten"}</span>
        </button>
        {l === "an" && (
          <button className="vgt-row" disabled={busy} onClick={testen}>
            <span className="ic"><VGTIcon name="sparkle" /></span>
            <span className="rt">Test schicken</span>
            <span className="rv"><VGTIcon name="chev" /></span>
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", margin: "6px 2px 0" }}>
        {meldung || (versandbereit
          ? hinweisZurLage(l)
          : "Mitteilungen sind noch nicht eingerichtet — wir sagen Bescheid, sobald es geht.")}
      </div>
    </>
  );
}

export function VGTProfil({ m, onBack, onLogout, onOpenFeedback }: { m: TeamModel; onBack: () => void; onLogout: () => void; onOpenFeedback?: () => void }) {
  const { user, summary } = m;
  const { theme, toggleTheme } = useTheme();
  if (!user) return null;
  return (
    <div className="vgt-screen">
      <div className="vgt-detail-head"><button className="vgt-backbtn" onClick={onBack}><VGTIcon name="back" /></button><div className="vgt-detail-title">Profil</div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        {/* Wer schon angemeldet ist, sieht den Balken ueber der Anmeldung nie
            mehr — im Profil bleibt er erreichbar. Laeuft die App bereits vom
            Startbildschirm, blendet er sich selbst aus. */}
        <InstallBanner />
        <div className="vgt-profile-hero">
          <div className="vgt-av lg">{user.initialen}</div>
          <div style={{ minWidth: 0 }}><div className="vgt-profile-name">{user.name}</div><div className="vgt-profile-role">{user.role}</div></div>
        </div>
        <div className="vgt-seclabel">Abrechnung</div>
        <div className="vgt-card">
          <div className="vgt-row"><span className="ic"><VGTIcon name="euro" /></span><span className="rt">Dein Stundensatz</span><span className="rv">{user.satz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/Std.</span></div>
          <div className="vgt-row"><span className="ic"><VGTIcon name="wallet" /></span><span className="rt">Für dich offen</span><span className="rv">{eur(summary.offenBetrag)}</span></div>
        </div>
        <VGTMitteilungen />
        <div className="vgt-seclabel">Darstellung</div>
        <div className="vgt-card">
          <button className="vgt-row" onClick={toggleTheme}>
            <span className="ic"><VGTIcon name="sparkle" /></span>
            <span className="rt">Ansicht</span>
            <span className="rv">{theme === "dark" ? "Dunkel" : "Hell"} · umschalten</span>
          </button>
        </div>
        <div className="vgt-seclabel">Konto</div>
        <div className="vgt-card">
          {user.email && <div className="vgt-row"><span className="ic"><VGTIcon name="mail" /></span><span className="rt">E-Mail</span><span className="rv">{user.email}</span></div>}
          {user.telefon && <div className="vgt-row"><span className="ic"><VGTIcon name="phone" /></span><span className="rt">Telefon</span><span className="rv">{user.telefon}</span></div>}
        </div>
        {onOpenFeedback && (
          <>
            <div className="vgt-seclabel">Feedback</div>
            <div className="vgt-card">
              <button className="vgt-row" onClick={onOpenFeedback}>
                <span className="ic"><VGTIcon name="chat" /></span>
                <span className="rt">Feedback geben</span>
                <span className="rv"><VGTIcon name="chev" /></span>
              </button>
            </div>
          </>
        )}
        <div className="vgt-card" style={{ marginTop: 4 }}><button className="vgt-row is-danger" onClick={onLogout}>Abmelden</button></div>
        <MVersionsZeile name="Vimaana Garten Team" />
      </div></div>
    </div>
  );
}

// ─────────────────────────── Feedback ──────────────────────────────────────
const FEEDBACK_PAGES: FeedbackPage[] = [
  { value: "allgemein", label: "Allgemein / app-weit" },
  { value: "heute", label: "Heute" },
  { value: "einsaetze", label: "Einsätze" },
  { value: "stunden", label: "Stunden" },
  { value: "geld", label: "Geld" },
  { value: "profil", label: "Profil" },
];

export function VGTFeedback({ onBack }: { onBack: () => void }) {
  const handleSubmit = async (p: FeedbackFormPayload) => {
    try {
      const res = await apiPost<{ logged: boolean; feedback_id?: string; error?: string }>(
        "/api/team/feedback",
        {
          beschreibung: p.beschreibung,
          page_path: p.page_path,
          screenshot_url: p.screenshot_url,
          context: p.context,
          app_version: "1.0",
        },
      );
      if (res.logged) return { ok: true };
      return { ok: false, error: res.error || "Feedback konnte nicht gespeichert werden." };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  };

  return (
    <div className="vgt-screen">
      <div className="vgt-detail-head"><button className="vgt-backbtn" onClick={onBack}><VGTIcon name="back" /></button><div className="vgt-detail-title">Feedback</div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5, marginBottom: 16 }}>
          Bug, Idee oder Hinweis zur Team-App? Schick es uns hier — wir nehmen es in die Weiterentwicklung auf.
        </p>
        <FeedbackForm
          pages={FEEDBACK_PAGES}
          onSubmit={handleSubmit}
          onDone={onBack}
        />
      </div></div>
    </div>
  );
}

// ─────────────────────────── Benachrichtigungen (abgeleitet) ──────────────
export function VGTNotifs({ m, onBack }: { m: TeamModel; onBack: () => void }) {
  // Beim Hinschauen ist es gelesen — ein Zaehler, der danach stehen bleibt,
  // ist nur laestig.
  useEffect(() => { if (m.nachrichtenUngelesen > 0) m.nachrichtenAlsGelesen(); }, []);

  const items: { kind: string; titel: string; detail: string }[] = [];
  const zu = m.hours.filter(h => h.status === "zu_bestaetigen");
  if (zu.length) items.push({ kind: "stunde", titel: `${zu.length} ${zu.length === 1 ? "Stunde wartet" : "Stunden warten"} auf dich`, detail: "Schau kurz im Reiter Stunden vorbei." });
  const einladungen = m.einsaetze.filter(e => e.einsatzStatus === "wartet_auf_helfer");
  if (einladungen.length) items.push({ kind: "plan", titel: `${einladungen.length} Job-${einladungen.length === 1 ? "Einladung" : "Einladungen"}`, detail: "Sag im Reiter Einsätze kurz Bescheid." });
  m.einsaetze.slice(0, 3).forEach(e => items.push({ kind: "plan", titel: e.projekt, detail: `${wtag(e.datum)}. ${tag(e.datum)}. ${monatKurz(e.datum)} · ${e.von}–${e.bis}` }));
  if (m.jobs.length) items.push({ kind: "job", titel: `${m.jobs.length} offene ${m.jobs.length === 1 ? "Stelle" : "Stellen"}`, detail: "Stehen bei den Einsätzen — melde dich, wenn du kannst." });
  const iconFor: Record<string, string> = { stunde: "clock", plan: "calendar", job: "briefcase" };
  return (
    <div className="vgt-screen">
      <div className="vgt-detail-head"><button className="vgt-backbtn" onClick={onBack}><VGTIcon name="back" /></button><div className="vgt-detail-title">Benachrichtigungen</div></div>
      <div className="vgt-scroll"><div className="vgt-pad">
        {/* Echte Nachrichten zuerst — sie sind das Einzige hier, was jemand
            geschickt hat. Der Rest darunter ist abgeleitet. */}
        {m.nachrichten.map(n => (
          <div key={n.id} className={"vgt-notif" + (n.gelesen ? "" : " is-neu")}>
            <span className={"nic " + (n.art === "einsatz_abgesagt" ? "absage" : "plan")}>
              <VGTIcon name={n.art === "einsatz_abgesagt" ? "info" : "calendar"} />
            </span>
            <div className="vgt-notif-main">
              <div className="vgt-notif-title">{n.titel}</div>
              <div className="vgt-notif-detail">{n.text}</div>
              <div className="vgt-notif-zeit">{relZeit(n.erstellt_am)}</div>
            </div>
          </div>
        ))}
        {items.length === 0 && m.nachrichten.length === 0 ? <div className="vgt-empty">Nichts Neues.</div> : items.map((n, i) => (
          <div key={i} className="vgt-notif">
            <span className={"nic " + n.kind}><VGTIcon name={iconFor[n.kind] || "bell"} /></span>
            <div className="vgt-notif-main"><div className="vgt-notif-title">{n.titel}</div><div className="vgt-notif-detail">{n.detail}</div></div>
          </div>
        ))}
      </div></div>
    </div>
  );
}

// ─────────────────────────── Sheets ───────────────────────────────────────

/** Woran die Zeit hängt — ein Stunden-Eintrag oder der heutige Einsatz.
 *  Beide Wege führen zu demselben Blatt und demselben Endpunkt. */
export type ZeitZiel = {
  slotId: string;
  projekt: string;
  /** Arbeitstag als `JJJJ-MM-TT`. */
  datum: string;
  /** Vorbelegung: Plan oder das, was schon erfasst ist. */
  von: string;
  bis: string;
  pause: number;
  /** Erster Eintrag? Dann zählt eine planungstreue Zeit sofort. */
  erstesMal: boolean;
};

/** Zeit eintragen — Beginn, Ende, Pause. Seit 20.08.2026 der einzige Weg, auf
 *  dem Ist-Zeit in die App kommt; die Stempeluhr ist abgeschaltet.
 *
 *  Vorbelegt ist immer der Plan (bzw. die zuletzt erfasste Zeit). Wer wie
 *  geplant gearbeitet hat, tippt nur auf „Speichern" — deshalb ist das Blatt
 *  auch dann abschickbar, wenn nichts geändert wurde. Genau das war beim
 *  früheren „Zeit anpassen" gesperrt, und dort ergab es Sinn: es war eine
 *  Korrektur. Ein Eintrag ist keine Korrektur. */
export function ZeitSheet({ ziel, onClose, onSubmit, busy }: {
  ziel: ZeitZiel; onClose: () => void;
  onSubmit: (von: string, bis: string, pause: number, notiz: string) => void; busy: boolean;
}) {
  const [von, setVon] = useState(ziel.von);
  const [bis, setBis] = useState(ziel.bis);
  const [pause, setPause] = useState(ziel.pause ?? 0);
  const [notiz, setNotiz] = useState("");
  const stepP = (d: number) => setPause(v => Math.max(0, Math.round((v + d) * 4) / 4));
  const toMin = (t: string) => { const [hh, mm] = (t || "").split(":").map(Number); return (hh || 0) * 60 + (mm || 0); };
  const brutto = (toMin(bis) - toMin(von)) / 60;
  const netto = Math.max(0, brutto - pause);
  const zeitOk = !!von && !!bis && toMin(bis) > toMin(von);
  // Ein Ende in der Zukunft weist der Server ab (400). Es hier schon zu sagen
  // ist freundlicher als eine Fehlermeldung nach dem Tippen.
  const heute = new Date().toISOString().slice(0, 10);
  const jetzt = new Date();
  const inZukunft = ziel.datum === heute
    && toMin(bis) > jetzt.getHours() * 60 + jetzt.getMinutes() + 15;
  const langesDatum = ziel.datum
    ? new Date(ziel.datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })
    : "";
  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Zeit eintragen</div>
        <div className="vgt-sheet-sub">{ziel.projekt}{langesDatum ? " · " + langesDatum : ""}. Von wann bis wann warst du dran?</div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><div className="vgt-field-label">Beginn</div><input className="vgt-input" type="time" value={von} onChange={e => setVon(e.target.value)} /></div>
          <div style={{ flex: 1 }}><div className="vgt-field-label">Ende</div><input className="vgt-input" type="time" value={bis} onChange={e => setBis(e.target.value)} /></div>
        </div>
        <div>
          <div className="vgt-field-label">Pause</div>
          <div className="vgt-stepper">
            <button onClick={() => stepP(-0.25)}><VGTIcon name="minus" style={{ width: 20, height: 20 }} /></button>
            <div className="v">{pause.toString().replace(".", ",")} h</div>
            <button onClick={() => stepP(0.25)}><VGTIcon name="plus" style={{ width: 20, height: 20 }} /></button>
          </div>
        </div>
        <div className="vgt-sheet-sub" style={{ marginTop: -2 }}>
          {!zeitOk ? "Das Ende muss nach dem Beginn liegen."
            : inZukunft ? "Das Ende liegt noch in der Zukunft — trag ein, bis wann du wirklich dran warst."
            : <>Ergibt <b>{std(netto)}</b> netto.</>}
        </div>
        <div>
          <div className="vgt-field-label">Kurz dazuschreiben (optional)</div>
          <textarea className="vgt-textarea" placeholder="z. B. War 15 Min. früher da und habe das Werkzeug verladen." value={notiz} onChange={e => setNotiz(e.target.value)} />
        </div>
        <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>
          {ziel.erstesMal
            ? "Passt die Zeit zum Plan, zählt sie direkt in deinem Guthaben. Weicht sie ab, schauen wir kurz drauf."
            : "Wir schauen kurz drauf — danach zählt die Zeit in deinem Guthaben."}
        </div></div>
        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy || !zeitOk || inZukunft} onClick={() => onSubmit(von, bis, pause, notiz.trim())}><VGTIcon name="check" /> Speichern</button>
        </div>
      </div>
    </div>
  );
}
export function NeuSheet({ projekte, projektpreise = [], onClose, onSubmit, busy }: {
  projekte: ProjektOption[]; onClose: () => void; busy: boolean;
  projektpreise?: Projektpreis[];
  onSubmit: (body: { projekt_id: string; datum: string; start: string; ende: string; pause_stunden: number; kommentar?: string; verguetungsart?: string; pauschale_id?: string }) => void;
}) {
  const t = new Date(); const p2 = (n: number) => String(n).padStart(2, "0");
  const todayIso = `${t.getFullYear()}-${p2(t.getMonth() + 1)}-${p2(t.getDate())}`;
  const [pid, setPid] = useState("");
  const [datum, setDatum] = useState(todayIso);
  const [start, setStart] = useState("08:00");
  const [ende, setEnde] = useState("16:00");
  const [pause, setPause] = useState("0.5");
  const [note, setNote] = useState("");
  const [art, setArt] = useState<"stundenlohn" | "pauschale">("stundenlohn");
  const [pauschaleId, setPauschaleId] = useState("");

  // Pauschalen, die für GENAU dieses Projekt mit dir vereinbart sind. Ohne
  // eine solche gibt es nichts zu wählen — dann bleibt es bei Stunden.
  const projektPauschalen = projektpreise.filter(
    p => p.projekt_id === pid && p.status === "bestaetigt",
  );
  // Projektwechsel setzt die Wahl zurück: eine Pauschale gehört zu ihrem
  // Projekt, sie darf nicht an ein anderes mitwandern.
  if (art === "pauschale" && projektPauschalen.length === 0) { setArt("stundenlohn"); setPauschaleId(""); }
  if (pauschaleId && !projektPauschalen.some(p => p.id === pauschaleId)) setPauschaleId("");

  // Ein Eintrag ohne Projekt kann niemandem zugerechnet werden und landet als
  // Rätsel bei der Bauleitung — deshalb ist das Projekt Pflicht.
  const gewaehlt = art === "stundenlohn" || !!pauschaleId || projektPauschalen.length === 1;
  const valid = !!pid && !!datum && ende > start && gewaehlt;
  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Zeit nachtragen</div>
        <div className="vgt-sheet-sub">Trag einen Arbeitstag nach, den wir noch nicht auf dem Schirm haben. Wir schauen kurz drauf.</div>
        <div>
          <div className="vgt-field-label">Projekt</div>
          <ProjektAuswahl projekte={projekte} value={pid} onChange={setPid} />
        </div>
        {/* Nur zeigen, wenn es für dieses Projekt überhaupt eine Pauschale gibt.
            Sonst wäre es eine Frage ohne zweite Antwort. */}
        {projektPauschalen.length > 0 && (
          <div>
            <div className="vgt-field-label">Wie wird das bezahlt?</div>
            <div className="vgt-rechnung-art" role="radiogroup" aria-label="Vergütung">
              <button type="button" role="radio" aria-checked={art === "stundenlohn"}
                      className={art === "stundenlohn" ? "is-active" : ""}
                      onClick={() => { setArt("stundenlohn"); setPauschaleId(""); }}>
                <strong>Nach Stunden</strong>
                <span>Wird mit deinem Stundensatz vergütet</span>
              </button>
              <button type="button" role="radio" aria-checked={art === "pauschale"}
                      className={art === "pauschale" ? "is-active" : ""}
                      onClick={() => { setArt("pauschale"); if (projektPauschalen.length === 1) setPauschaleId(projektPauschalen[0].id); }}>
                <strong>Über die Pauschale</strong>
                <span>Die Zeit steckt im Festpreis, kein Stundenlohn obendrauf</span>
              </button>
            </div>
            {art === "pauschale" && projektPauschalen.length > 1 && (
              <div className="vgt-pausch-wahl">
                {projektPauschalen.map(p => (
                  <button key={p.id} type="button"
                          className={"vgt-pausch-opt" + (pauschaleId === p.id ? " is-active" : "")}
                          onClick={() => setPauschaleId(p.id)}>
                    <span>{p.bezeichnung || "Pauschale"}</span><strong>{eur(p.betrag)}</strong>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div>
          <div className="vgt-field-label">Datum</div>
          <input className="vgt-input" type="date" value={datum} max={todayIso} onChange={e => setDatum(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><div className="vgt-field-label">Von</div><input className="vgt-input" type="time" value={start} onChange={e => setStart(e.target.value)} /></div>
          <div style={{ flex: 1 }}><div className="vgt-field-label">Bis</div><input className="vgt-input" type="time" value={ende} onChange={e => setEnde(e.target.value)} /></div>
          <div style={{ width: 92 }}><div className="vgt-field-label">Pause h</div><input className="vgt-input" type="number" step="0.25" min="0" value={pause} onChange={e => setPause(e.target.value)} /></div>
        </div>
        <div><div className="vgt-field-label">Notiz (optional)</div><textarea className="vgt-textarea" value={note} onChange={e => setNote(e.target.value)} placeholder="z. B. was gemacht wurde" /></div>
        {projekte.length === 0 && <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Zeit geht nur zu Projekten, bei denen du dabei warst — aktuell ist keines da.</div></div>}
        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy || !valid} onClick={() => onSubmit({
            projekt_id: pid, datum, start, ende,
            pause_stunden: Math.max(0, parseFloat(pause.replace(",", ".")) || 0),
            kommentar: note.trim() || undefined,
            verguetungsart: art,
            pauschale_id: art === "pauschale"
              ? (pauschaleId || projektPauschalen[0]?.id || "")
              : undefined,
          })}>
            <VGTIcon name="check" /> Erfassen
          </button>
        </div>
      </div>
    </div>
  );
}

/** Rücknahme einer Zusage oder Bewerbung. Nach einem zugesagten Job fragt die
 *  App nach einem Grund — die Bauleitung muss umplanen und will wissen, woran
 *  sie ist. Erzwungen wird er nicht (Punkt 7 gilt überall). */
export function AbsageSheet({ einsatz, onClose, onSubmit, busy }: {
  einsatz: Einsatz; onClose: () => void; onSubmit: (grund: string) => void; busy: boolean;
}) {
  const [grund, setGrund] = useState("");
  const warZugesagt = einsatz.einsatzStatus === "zugesagt";
  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">{warZugesagt ? "Job absagen" : "Doch nicht dabei"}</div>
        <div className="vgt-sheet-sub">
          {einsatz.projekt} · {wtag(einsatz.datum)}. {tag(einsatz.datum)}. {monatKurz(einsatz.datum)} · {einsatz.von}–{einsatz.bis}
        </div>
        {warZugesagt && (
          <div>
            <div className="vgt-field-label">Was ist dazwischengekommen? (optional)</div>
            <textarea className="vgt-textarea" placeholder="z. B. Bin krank geworden." value={grund} onChange={e => setGrund(e.target.value)} />
          </div>
        )}
        <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>
          {warZugesagt
            ? "Wir sehen es sofort und können jemand anderen fragen. Je früher, desto besser."
            : "Wir nehmen dich wieder raus. Melde dich gern noch mal, solange der Platz frei ist."}
        </div></div>
        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Behalten</button>
          <button className="vgt-cta" disabled={busy} onClick={() => onSubmit(grund.trim())}>
            {warZugesagt ? "Absagen" : "Zurückziehen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function InteresseSheet({ job, onClose, onSubmit, busy }: { job: Job; onClose: () => void; onSubmit: () => void; busy: boolean }) {
  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Ich bin dabei</div>
        <div className="vgt-sheet-sub">{job.projekt} · {wtag(job.datum)}. {tag(job.datum)}. {monatKurz(job.datum)} · {job.von}–{job.bis}</div>
        <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Du sagst uns damit, dass du kannst. Wir teilen ein und melden uns bei dir.</div></div>
        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy} onClick={onSubmit}><VGTIcon name="check" /> Ja, ich kann</button>
        </div>
      </div>
    </div>
  );
}

const HR_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/heic"];
const HR_MAX = 20 * 1024 * 1024;

const FOTO_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif", "image/webp"];
const FOTO_MAX_COUNT = 20;

/** Doku-Fotos zu einem Projekt hochladen — Mehrfachauswahl mit Vorschau. */
/** Woher der Upload kommt: Projekt plus — wenn bekannt — der konkrete Einsatz.
 *  Aus dem Slot leitet der Server Arbeitstag UND Projekt ab; das ist der Weg,
 *  auf dem nichts falsch eingetippt werden kann. */
export type FotoZiel = { projektId: string; slotId?: string; datum?: string };

export function FotoUploadSheet({ projekte, initialProjektId, slotId, datum, onClose, onSubmit, busy }: {
  projekte: ProjektOption[]; initialProjektId?: string;
  slotId?: string; datum?: string;
  onClose: () => void; busy: boolean;
  onSubmit: (args: FotoUploadArgs) => void;
}) {
  // Aus einem Einsatz heraus geöffnet (initialProjektId) → Projekt steht fest.
  // Sonst sucht der Helfer es sich; die Liste enthält nur Projekte, an denen er
  // eingesetzt war — dieselbe Menge, die das Upload-Backend erlaubt.
  const fixed = !!initialProjektId;
  const fixedTitle = fixed
    ? (projekte.find(p => p.id === initialProjektId)?.title || "Aktuelles Projekt")
    : "";
  const [pid, setPid] = useState(initialProjektId || "");
  // Stabile Vorschau-URLs: pro Datei einmal erzeugen, erst beim Entfernen freigeben.
  const [files, setFiles] = useState<{ f: File; url: string }[]>([]);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState("");

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    let fehler = "";
    for (const f of Array.from(list)) {
      if (!FOTO_TYPES.includes(f.type)) { fehler = `${f.name}: nur JPG, PNG, HEIC oder WEBP.`; continue; }
      if (f.size > HR_MAX) { fehler = `${f.name}: zu groß (max. 20 MB).`; continue; }
      if (next.length >= FOTO_MAX_COUNT) { fehler = `Max. ${FOTO_MAX_COUNT} Fotos pro Upload.`; break; }
      next.push({ f, url: URL.createObjectURL(f) });
    }
    setErr(fehler); setFiles(next);
  };
  const removeAt = (i: number) => setFiles(fs => {
    const gone = fs[i]; if (gone) URL.revokeObjectURL(gone.url);
    return fs.filter((_, idx) => idx !== i);
  });
  /** Fallback, falls der Objekt-URL nicht dekodiert (exotische File-Quellen):
   *  Bytes per FileReader als Data-URL nachladen — einmal pro Datei. */
  const onThumbError = (i: number) => {
    const x = files[i];
    if (!x || x.url.startsWith("data:")) return;
    const rd = new FileReader();
    rd.onload = () => setFiles(fs => fs.map((y, idx) => idx === i ? { ...y, url: String(rd.result) } : y));
    rd.readAsDataURL(x.f);
  };

  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Fotos hochladen</div>
        <div className="vgt-sheet-sub">
          {/* Der Tag wird gezeigt, nicht abgefragt: er steht durch den Einsatz
              fest. Und keine Vorher/Nachher-Wahl — Bilder vom Arbeitstag sind
              der Verlauf, da gibt es nichts zu entscheiden.

              `langDatum` statt der rohen ISO-Form: „2026-08-24" liest niemand
              als Montag. Der Tag steht hier, damit der Helfer sieht, an welchen
              Arbeitstag die Bilder gehen — nicht an den Tag des Hochladens
              (siehe docs/system/13-TEAM.md, Punkt 5). */}
          {fixed
            ? <>Für <b>{fixedTitle}</b>{datum ? <> · <b>{langDatum(datum)}</b></> : null}. Sie landen im Verlauf des Projekts.</>
            : "Deine Fotos landen im Verlauf des Projekts. Wähl das Projekt und die Bilder."}
        </div>
        {(!fixed && projekte.length === 0) ? (
          <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Fotos gehen nur zu Projekten, bei denen du dabei warst — aktuell ist keines da.</div></div>
        ) : (<>
          {!fixed && (
            <div>
              <div className="vgt-field-label">Projekt</div>
              <ProjektAuswahl projekte={projekte} value={pid} onChange={setPid} />
            </div>
          )}
          <label
            className={"vgt-drop" + (drag ? " is-drag" : "")}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          >
            <input type="file" accept="image/*" multiple hidden
                   onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            <div className="vgt-drop-empty">
              <span className="ic"><VGTIcon name="camera" /></span>
              <div className="t">Fotos hierher ziehen<br /><span className="sub">oder tippen — Kamera oder Galerie · mehrere möglich</span></div>
            </div>
          </label>
          {files.length > 0 && (
            <div className="vgt-fotogrid">
              {files.map((x, i) => (
                <div key={x.f.name + i} className="vgt-fotothumb">
                  <img src={x.url} alt={x.f.name} onError={() => onThumbError(i)} />
                  <button className="rm" onClick={() => removeAt(i)} title="Entfernen"><VGTIcon name="minus" /></button>
                </div>
              ))}
            </div>
          )}
          {err && <div className="vgt-login-err">{err}</div>}
          <div className="vgt-actions">
            <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
            <button className="vgt-cta" disabled={busy || !pid || files.length === 0}
                    onClick={() => onSubmit({ projektId: pid, slotId, datum, dateien: files.map(x => x.f) })}>
              {busy ? "Lädt hoch …" : <><VGTIcon name="arrowup" /> {files.length > 0 ? `${files.length} ${files.length === 1 ? "Foto" : "Fotos"} hochladen` : "Hochladen"}</>}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}

export function RechnungUploadSheet({ onClose, onSubmit, busy, initialFile, ersetzen, onErsetzen, projektpreise = [] }: {
  onClose: () => void; busy: boolean;
  onSubmit?: (args: RechnungUploadArgs) => void;
  initialFile?: File | null;
  ersetzen?: boolean;
  onErsetzen?: (file: File) => void;
  projektpreise?: TeamModel["projektpreise"];
}) {
  const [file, setFile] = useState<File | null>(
    initialFile && HR_TYPES.includes(initialFile.type) && initialFile.size <= HR_MAX ? initialFile : null);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState("");
  const [rnr, setRnr] = useState("");
  const [datum, setDatum] = useState("");
  const [betrag, setBetrag] = useState("");
  const [notiz, setNotiz] = useState("");
  const [rechnungsart, setRechnungsart] = useState<"stunden" | "pauschale">("stunden");
  const [projektId, setProjektId] = useState("");
  // 'liest' sperrt nur das Absenden, solange die Erkennung laeuft.
  const [liest, setLiest] = useState(false);
  const bestaetigtePauschalen = projektpreise.filter((p) => p.status === "bestaetigt");
  const pauschalProjekte = [...new Map(
    bestaetigtePauschalen.map((p) => [p.projekt_id, p.projekt_title || "Projekt"]),
  )];
  const gewaehltePauschalen = bestaetigtePauschalen.filter((p) => p.projekt_id === projektId);

  /** Datei uebernehmen und sofort lesen lassen.
   *
   *  Betrag und Datum stehen auf jeder Rechnung — sie abzutippen ist Arbeit,
   *  die niemand braucht. Gelesen wird deshalb IMMER, sobald die Datei da ist,
   *  und das Ergebnis wird eingetragen; korrigieren kann der Helfer danach in
   *  Ruhe. (Bis 12.08.2026 fuellte die Erkennung nur leere Felder — wer eine
   *  Zahl schon getippt hatte, bekam nie zu sehen, was auf dem Blatt steht.)
   *  Beim Ersetzen wird nicht gelesen: dort aendert sich nur die Datei, die
   *  Zahlen am Beleg bleiben. */
  const accept = (f: File | null) => {
    if (!f) return;
    if (!HR_TYPES.includes(f.type)) { setErr("Nur PDF, JPG, PNG oder HEIC."); return; }
    if (f.size > HR_MAX) { setErr("Datei zu groß (max. 20 MB)."); return; }
    setErr(""); setFile(f);
    if (ersetzen) return;
    setLiest(true);
    rechnungAnalysieren(f)
      .then(a => {
        if (a.betrag > 0) setBetrag(String(a.betrag).replace(".", ","));
        if (a.datum) setDatum(a.datum);
      })
      .finally(() => setLiest(false));
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false);
    accept(e.dataTransfer.files?.[0] || null);
  };
  const fmtSize = (b: number) => b < 1024 * 1024 ? Math.round(b / 1024) + " KB" : (b / 1048576).toFixed(1) + " MB";

  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">{ersetzen ? "Rechnung ersetzen" : "Rechnung hochladen"}</div>
        <div className="vgt-sheet-sub">{ersetzen
          ? "Lad die neue Datei hoch — sie ersetzt die alte. Betrag und Datum bleiben, die alte Datei heben wir auf."
          : "Lad deine Rechnung hoch. Sie landet bei uns im Belegstapel, wir kümmern uns drum."}</div>

        {!ersetzen && (
          <div className="vgt-rechnung-art" role="radiogroup" aria-label="Rechnungsart">
            <button type="button" role="radio" aria-checked={rechnungsart === "stunden"} className={rechnungsart === "stunden" ? "is-active" : ""} onClick={() => { setRechnungsart("stunden"); setProjektId(""); }}>
              <strong>Geleistete Stunden</strong>
              <span>Für deine bestätigten Stunden</span>
            </button>
            <button type="button" role="radio" aria-checked={rechnungsart === "pauschale"} className={rechnungsart === "pauschale" ? "is-active" : ""} onClick={() => setRechnungsart("pauschale")}>
              <strong>Projektpauschale</strong>
              <span>Für einen Festpreis, den wir vereinbart haben</span>
            </button>
          </div>
        )}

        {!ersetzen && rechnungsart === "pauschale" && (
          pauschalProjekte.length ? (
            <div>
              <div className="vgt-field-label">Projekt mit vereinbarter Pauschale</div>
              <ProjektAuswahl projekte={pauschalProjekte.map(([id, title]) => ({ id, title }))}
                              value={projektId} onChange={setProjektId} />
              {gewaehltePauschalen.length > 0 && (
                <div className="vgt-pauschal-preview">
                  {gewaehltePauschalen.map((p) => (
                    <span key={p.id}><strong>{p.bezeichnung || "Pauschale"}</strong>{eur(p.betrag)}</span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Für dich ist noch keine Pauschale vereinbart. Sprich uns kurz an, bevor du sie in Rechnung stellst.</div></div>
          )
        )}

        <label
          className={"vgt-drop" + (drag ? " is-drag" : "") + (file ? " is-set" : "")}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
        >
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/*" hidden
                 onChange={e => accept(e.target.files?.[0] || null)} />
          {file ? (
            <div className="vgt-drop-file">
              <span className="ic"><VGTIcon name="doc" /></span>
              <div className="m"><div className="n">{file.name}</div><div className="s">{fmtSize(file.size)} · tippen zum Ändern</div></div>
            </div>
          ) : (
            <div className="vgt-drop-empty">
              <span className="ic"><VGTIcon name="arrowup" /></span>
              <div className="t">Datei hierher ziehen<br /><span className="sub">oder tippen zum Auswählen · PDF, JPG, PNG</span></div>
            </div>
          )}
        </label>
        {err && <div className="vgt-login-err" style={{ marginTop: 8 }}>{err}</div>}

        {!ersetzen && (<>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><div className="vgt-field-label">Rechnungsnr. (optional)</div><input className="vgt-input" value={rnr} onChange={e => setRnr(e.target.value)} placeholder="z. B. 2026-014" /></div>
          <div style={{ width: 120 }}><div className="vgt-field-label">Betrag €{liest ? " · Clara liest …" : ""}</div><input className="vgt-input" type="number" inputMode="decimal" step="0.01" min="0" value={betrag} onChange={e => setBetrag(e.target.value)} placeholder="brutto" /></div>
        </div>
        <div><div className="vgt-field-label">Rechnungsdatum (optional)</div><input className="vgt-input" type="date" value={datum} onChange={e => setDatum(e.target.value)} /></div>
        <div><div className="vgt-field-label">Notiz (optional)</div><textarea className="vgt-textarea" value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z. B. Zeitraum / Tätigkeit" /></div>

        </>)}

        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy || liest || !file || (rechnungsart === "pauschale" && !projektId)} onClick={() => {
            if (!file) return;
            if (ersetzen) { onErsetzen?.(file); return; }
            onSubmit?.({
              datei: file,
              rechnungsart,
              projekt_id: rechnungsart === "pauschale" ? projektId : undefined,
              rechnungsnummer: rnr.trim() || undefined,
              datum: datum || undefined,
              betrag: betrag ? Math.max(0, parseFloat(betrag.replace(",", ".")) || 0) : undefined,
              notiz: notiz.trim() || undefined,
            });
          }}>
            {busy ? (ersetzen ? "Ersetze …" : "Lädt hoch …") : <><VGTIcon name="arrowup" /> {ersetzen ? "Ersetzen" : "Hochladen"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
